import { logger } from "@/modules/logger";
import { uuidModule } from "@/modules/uuid";
import { ApiKeyModelData, ApiKeyUpsertedSyncUpdateValue } from "@/store/api-keys/types";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { generateDefaultOwnerScopes } from "@/store/sync/operations/helpers/common";
import { IUpdateApiKeyOperation } from "@/store/sync/operations/types";
import { SyncModelData, SyncOperationKind, OptimisticSyncUpdate } from "@/store/sync/types";
import { DateTime } from "luxon";

export class UpdateApiKeyOperation extends BaseSyncOperation<IUpdateApiKeyOperation> {
  get operationKind(): SyncOperationKind {
    return "UPDATE_API_KEY";
  }

  public async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const optimisticUpdates: OptimisticSyncUpdate<SyncModelData>[] = [];
    const apiKeyUpdate = this.generateUpdateApiKeyUpdate();

    if (apiKeyUpdate) {
      optimisticUpdates.push(apiKeyUpdate);
    }

    return optimisticUpdates;
  }

  async triggerRecompute() {
    await this.store.apiKeys.recompute(this.payload.id);
  }

  protected generateUpdateApiKeyUpdate(): OptimisticSyncUpdate<ApiKeyModelData> | null {
    const observable = this.store.apiKeys.get(this.payload.id);

    if (!observable) {
      logger.debug({
        message: "Attempted to update API key, but it was not found",
        info: { apiKeyId: this.payload.id },
      });

      return null;
    }

    const value: ApiKeyUpsertedSyncUpdateValue = {
      model_id: this.payload.id,
      model_kind: "API_KEY",
      model_version: observable?.modelVersion ?? 0,
      model_data: {
        id: this.payload.id,
        title: this.payload.title ?? observable.title,
        description: this.payload.description ?? observable.description,
        created_at: observable.createdAt.toISO() ?? DateTime.now().toISO(),
        is_revoked: observable.isRevoked,
        redacted_token: observable.redactedToken,
      },
      model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
    };

    const syncUpdate: OptimisticSyncUpdate<ApiKeyModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value,
    };

    return syncUpdate;
  }
}
